import _ from "lodash";

export const VALIDATE_RULES = {
    required: (value): boolean => {
        return value && /.+/.test(value);
    },
    phone: (value): boolean => {
        return value && /^[1][3-9][0-9]{9}$/.test(value);
    },
    simplePassword: (value): boolean => {
        return value && /^.{6,12}$/.test(value);
    }
};

function _syncValidate(field: string, value, rule: ValidatorRule): Promise<ValidatorError> {
    return new Promise<ValidatorError>((resolve, reject) => {
        if (_.isString(rule.is)) {
            const validate = _.get(VALIDATE_RULES, rule.is, () => true) as Function;
            if (validate(value)) {
                resolve({ valid: true, field, message: "" });
            } else {
                reject({ valid: false, field, message: rule.message });
            }
        } else if (_.isRegExp(rule.is)) {
            if (rule.is.test(value)) {
                resolve({ valid: true, field, message: "" });
            } else {
                reject({ valid: false, field, message: rule.message });
            }
        } else {
            reject({ valid: true, field, message: "" });
        }
    });
}

function _validate(field, value, ruleMap: { [key: string]: object[] }): Promise<ValidatorError> {
    const rules = _.get(ruleMap, field) as ValidatorRule[];
    const promises: (() => Promise<ValidatorError>)[] = rules.map(rule => {
        if (_.isFunction(rule.is)) {
            return () => rule.is(field, rule.message) as Promise<ValidatorError>;
        }
        return () => _syncValidate(field, value, rule);
    });

    const promiseQueue = _.reduce(promises, (prevPromise, nextPromise) => {
        return () =>
            prevPromise().then(() => {
                return nextPromise();
            });
    }) as () => Promise<ValidatorError>;

    return promiseQueue()
        .then(() => {
            return Promise.resolve({ field, valid: true, message: "" });
        })
        .catch(error => {
            return Promise.reject(error);
        });
}

class Validator {
    errors: object;
    rules: { [key: string]: object[] };

    constructor(rules: { [key: string]: object[] }) {
        this.rules = rules;
        this.errors = _.mapValues(rules, () => "");
    }

    setErrors(errors: ValidatorError[], othersKey = "error"): ValidatorError[] {
        if (_.isEmpty(errors)) return [];
        const otherError: ValidatorError[] = [];
        _.forEach(errors, error => {
            if (_.has(this.errors, error.field)) {
                _.set(this.errors, error.field, error.message);
            } else {
                _.set(this.errors, othersKey, error.message);
                otherError.push(error);
            }
        });
        return otherError;
    }

    clearErrors(field = "") {
        if (_.isEmpty(field)) {
            _.mapValues(this.rules, () => "");
        } else {
            _.set(this.errors, field, "");
        }
    }

    emit(field, value): Promise<ValidatorError> {
        return _validate(field, value, this.rules)
            .then(success => {
                this.clearErrors(field);
                return success;
            })
            .catch(error => {
                this.setErrors([error]);
                return error;
            });
    }

    validates(root: object, fields: string[] = []): Promise<ValidatorError[]> {
        if (_.isEmpty(fields)) {
            fields = _.keys(root);
        }
        const promises: Promise<ValidatorError>[] = fields.map(field => {
            const value = _.get(root, field);
            return _validate(field, value, this.rules);
        });
        return Promise.allSettled(promises).then(allResults => {
            const errors: ValidatorError[] = allResults.map(settled => _.get(settled, "reason")).filter(val => val);
            const valid: boolean = _.isEmpty(errors);
            this.clearErrors();
            if (!valid) {
                return Promise.reject(errors);
            }
            return Promise.resolve([]);
        });
    }
}

interface ValidatorRule {
    is: (field: string, message: string) => string | RegExp | Promise<ValidatorError>;
    type: boolean;
    message: string;
}

export interface ValidatorError {
    field: string;
    valid: boolean;
    message: string;
}

export function Validators(ruleMap: { [key: string]: object[] }): Validator {
    return new Validator(ruleMap);
}
